<i18n locale="en">
  {
    "close": "Close"
  }
</i18n>

<template>
  <div
    :class="[
      'dialog-heading align-center flex-row justify-between gap-6',
      { 'dialog-heading--no-border': hideBorder },
    ]"
  >
    <div class="align-center flex-row gap-2">
      <slot name="icon-left" />
      <div class="flex-col gap-0.5">
        <OwnType v-if="title" :text="title" variant="title-regular" />
        <OwnType
          v-if="subtitle"
          :text="subtitle"
          variant="paragraph-small"
          color="secondary"
        />
      </div>
    </div>
    <button
      v-if="!isPersistent && !props.hideCloseButton"
      :aria-label="t('close')"
      class="dialog-heading__close"
      @click="handleCancel"
    >
      <PhX size="18" weight="bold" />
    </button>
  </div>
</template>

<script lang="ts" setup>
import { PhX } from '@phosphor-icons/vue'
import { inject, ref, computed, useAttrs, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { OwnType } from '../OwnType'

const { t } = useI18n()
const attrs = useAttrs()

const props = withDefaults(
  defineProps<{
    hideBorder?: boolean
    hideCloseButton?: boolean
    subtitle?: string
    title?: string
    useNewHeaderStyles?: boolean
  }>(),
  {
    hideBorder: false,
    hideCloseButton: false,
    subtitle: undefined,
    title: undefined,
    useNewHeaderStyles: false,
  }
)

// Inject the persistent state from parent dialog
const dialogPersistentRef = inject<Ref<boolean>>('dialogPersistent', ref(false))
const isPersistent = computed(() => dialogPersistentRef.value)

/**
 * Inject the cancel handler from parent OwnDialog.
 * This provides automatic close functionality without requiring explicit @cancel listeners.
 */
const injectedCancel = inject<(() => void) | undefined>(
  'dialogCancel',
  undefined
)

const emit = defineEmits<(event: 'cancel') => void>()

// Check if there's an explicit @cancel listener
const hasExplicitCancelListener = computed(() => 'onCancel' in attrs)

/**
 * Handle the close button click with smart fallback behavior:
 * 1. If explicit @cancel listener exists, use it (allows override)
 * 2. Otherwise, use injected handler from parent OwnDialog (automatic)
 * 3. Fallback to emit for backwards compatibility
 */
const handleCancel = () => {
  if (hasExplicitCancelListener.value) {
    // If there's an explicit listener, emit the event
    emit('cancel')
  } else if (injectedCancel) {
    // Otherwise, use the injected handler from parent dialog
    injectedCancel()
  } else {
    // Fallback: emit anyway (backwards compatibility)
    emit('cancel')
  }
}
</script>

<style lang="scss" scoped>
.dialog-heading {
  border-bottom: 0.5px solid var(--divider);
  padding: 14px 16px;

  &--no-border {
    border-bottom: none;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    border-radius: 6px;
    color: var(--text-color-secondary);
    transition:
      background-color 0.15s ease-out,
      color 0.15s ease-out;

    &:hover {
      background-color: var(--background-quarternary);
      color: var(--text-color-primary);
    }

    &:active {
      transform: scale(0.95);
    }
  }
}
</style>
